// Phase-1 Touchpoints — create / edit modal.
//
// Manual Account-touchpoint form. The same modal handles create
// (``mode === "create"``) and edit (``mode === "edit"``); the parent
// hands an existing Touchpoint when editing so the controlled inputs
// hydrate.

import { useContext, useEffect, useMemo, useState } from "react";

import { Button, Modal } from "@/design-system";
import { AuthContext } from "@/features/auth/AuthContext";

import type {
  Touchpoint,
  TouchpointCreateInput,
  TouchpointKind,
} from "./types";
import { TOUCHPOINT_KIND_LABEL } from "./types";


// Major US IANA zones, ordered west → east the way brokers usually
// scan them. Alaska + Hawaii sit at the bottom because most BeneCRM
// brokers are continental-US — keep the common picks above the fold.
// Phoenix is its own entry because Arizona doesn't observe DST and
// rendering ``America/Phoenix`` differently from ``America/Denver``
// matters for after-hours scheduling.
const US_TIMEZONES: Array<{ value: string; label: string }> = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Mountain — no DST (Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];


export interface TouchpointFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  touchpoint?: Touchpoint | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (values: TouchpointCreateInput) => void;
}


interface FormState {
  kind: TouchpointKind;
  subject: string;
  scheduled_at: string;     // ``<input type="datetime-local">`` raw value
  duration_minutes: string;
  time_zone: string;
  notes: string;
  attendees: string;       // comma-separated; split on submit
}


function blank(defaultTimeZone: string): FormState {
  return {
    kind: "meeting",
    subject: "",
    scheduled_at: "",
    duration_minutes: "30",
    time_zone: defaultTimeZone,
    notes: "",
    attendees: "",
  };
}


function fromTouchpoint(tp: Touchpoint): FormState {
  return {
    kind: tp.kind,
    subject: tp.subject ?? "",
    scheduled_at: toLocalInputValue(tp.scheduled_at),
    duration_minutes:
      tp.duration_minutes != null ? String(tp.duration_minutes) : "",
    time_zone: tp.time_zone ?? "",
    notes: tp.body ?? "",
    attendees: (tp.participants ?? []).join(", "),
  };
}


/** Convert an ISO datetime string to ``YYYY-MM-DDTHH:mm`` for the
 * browser's ``datetime-local`` input. Returns "" on invalid input
 * so the input renders blank rather than NaN.
 */
function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Pad to 2-digit segments for the input's expected format.
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}


function localInputToISO(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}


const KIND_OPTIONS: TouchpointKind[] = ["meeting", "call", "email", "other"];


export function TouchpointFormModal({
  open, mode, touchpoint, saving, error, onClose, onSubmit,
}: TouchpointFormModalProps) {
  // Read AuthContext directly so a parent that mounts the modal
  // outside an AuthProvider (notably the AccountDetailPage tests)
  // falls back to the canonical default timezone instead of crashing.
  const auth = useContext(AuthContext);
  const defaultTimeZone = auth?.user?.timezone || "America/New_York";

  const [form, setForm] = useState<FormState>(() =>
    touchpoint ? fromTouchpoint(touchpoint) : blank(defaultTimeZone),
  );

  // Re-hydrate when the modal opens or the editing target changes so a
  // create-then-edit flow doesn't show stale fields.
  useEffect(() => {
    if (!open) return;
    setForm(
      touchpoint ? fromTouchpoint(touchpoint) : blank(defaultTimeZone),
    );
  }, [open, touchpoint, defaultTimeZone]);

  const submitLabel = useMemo(
    () => (mode === "edit" ? "Save changes" : "Log Touchpoint"),
    [mode],
  );

  const onSubmitClick = () => {
    const attendees = form.attendees
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    const input: TouchpointCreateInput = {
      kind: form.kind,
      subject: form.subject.trim() || null,
      scheduled_at: localInputToISO(form.scheduled_at),
      duration_minutes:
        form.duration_minutes.trim() === ""
          ? null
          : Number.parseInt(form.duration_minutes, 10) || 0,
      time_zone: form.time_zone.trim() || null,
      notes: form.notes.trim() || null,
      attendees,
    };
    onSubmit(input);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Touchpoint" : "Log Touchpoint"}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="tertiary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="touchpoint-form-submit"
            onClick={onSubmitClick}
            disabled={saving}
          >
            {saving ? "Saving…" : submitLabel}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Type">
          <select
            data-testid="touchpoint-form-kind"
            value={form.kind}
            onChange={(e) =>
              setForm((s) => ({
                ...s, kind: e.target.value as TouchpointKind,
              }))
            }
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {TOUCHPOINT_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Subject">
          <input
            type="text"
            data-testid="touchpoint-form-subject"
            value={form.subject}
            onChange={(e) =>
              setForm((s) => ({ ...s, subject: e.target.value }))
            }
            placeholder="Quarterly review, renewal kickoff, …"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="When">
            <input
              type="datetime-local"
              data-testid="touchpoint-form-scheduled-at"
              value={form.scheduled_at}
              onChange={(e) =>
                setForm((s) => ({ ...s, scheduled_at: e.target.value }))
              }
              className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
            />
          </Field>
          <Field label="Duration (min)">
            <input
              type="number"
              min={0}
              data-testid="touchpoint-form-duration"
              value={form.duration_minutes}
              onChange={(e) =>
                setForm((s) => ({
                  ...s, duration_minutes: e.target.value,
                }))
              }
              className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
            />
          </Field>
        </div>

        <Field label="Time zone">
          <select
            data-testid="touchpoint-form-time-zone"
            value={form.time_zone}
            onChange={(e) =>
              setForm((s) => ({ ...s, time_zone: e.target.value }))
            }
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
          >
            {/* If the editing target carries a non-US zone (legacy
                rows, or a user who picked something exotic in a prior
                pass), keep that option present so the dropdown
                doesn't silently rewrite their data on save. */}
            {form.time_zone &&
              !US_TIMEZONES.some((tz) => tz.value === form.time_zone) && (
                <option value={form.time_zone}>{form.time_zone}</option>
              )}
            {US_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Attendees" hint="Comma-separated names. Free text for now.">
          <input
            type="text"
            data-testid="touchpoint-form-attendees"
            value={form.attendees}
            onChange={(e) =>
              setForm((s) => ({ ...s, attendees: e.target.value }))
            }
            placeholder="Alice Smith, Bob Lee"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
          />
        </Field>

        <Field label="Agenda / notes">
          <textarea
            rows={4}
            data-testid="touchpoint-form-notes"
            value={form.notes}
            onChange={(e) =>
              setForm((s) => ({ ...s, notes: e.target.value }))
            }
            placeholder="What to cover, links, prior context…"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
          />
        </Field>

        {error && (
          <p
            role="alert"
            data-testid="touchpoint-form-error"
            className="text-xs font-medium text-ember"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}


function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-primary">{label}</span>
      {children}
      {hint && (
        <span className="text-[11px] text-text-tertiary">{hint}</span>
      )}
    </label>
  );
}
